// src/components/Popups/StudentInfo/studentPracticalExams.js
//
// Examenele practice ale unui elev, pentru tab-ul „Examen → Practic" din
// profilul deschis de admin/manager. Elevul isi vede istoricul prin
// /my-history; pentru un alt elev exista doar lista de admin, asa ca cerem
// toate statusurile (endpoint-ul intoarce implicit doar programarile active,
// ca in calendarul de examen) si filtram dupa elev.

import { getAdminPracticalExamReservations } from "../../../api/practicalExamSlotsService";
import {
   getPracticalExamStatusLabel,
   normalizePracticalExamHour,
   normalizePracticalExamSector,
   readPracticalExamReservationStatus,
   resolvePracticalExamReservationApiId,
} from "../../../constants/practicalExamCalendar";

const STATUS_FILTERS = ["PASSED", "FAILED", "ABSENT", "CANCELLED"];

const DATE_KEYS = [
   "dateKey",
   "date",
   "slotDate",
   "slot_date",
   "examDate",
   "exam_date",
   "startAt",
   "start_at",
   "startsAt",
   "starts_at",
];

const TIME_KEYS = [
   "timeKey",
   "time",
   "slotTime",
   "slot_time",
   "examTime",
   "exam_time",
   "startTime",
   "start_time",
   "startAt",
   "start_at",
   "startsAt",
   "starts_at",
];

function readFirst(source, keys, fallback = null) {
   if (!source || typeof source !== "object") return fallback;
   for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== "") return value;
   }
   return fallback;
}

function readSlot(reservation) {
   return (
      readFirst(reservation, [
         "slot",
         "examSlot",
         "practicalExamSlot",
         "practical_exam_slot",
         "PracticalExamSlot",
      ]) || null
   );
}

function readRaw(reservation) {
   const raw = reservation?.raw;
   return raw && typeof raw === "object" ? raw : {};
}

export function readPracticalExamStudentId(reservation) {
   const raw = readRaw(reservation);
   const person =
      readFirst(reservation, ["user", "student", "client", "person", "userInfo"]) ||
      readFirst(raw, ["user", "student", "client", "person", "userInfo"]);

   const id = readFirst(
      reservation,
      ["userId", "user_id", "studentId", "student_id"],
      readFirst(
         raw,
         ["userId", "user_id", "studentId", "student_id"],
         readFirst(person, ["id", "userId", "user_id"]),
      ),
   );

   return id == null ? "" : String(id).trim();
}

export function readPracticalExamDateKey(reservation) {
   const value = readFirst(
      reservation,
      DATE_KEYS,
      readFirst(readSlot(reservation), DATE_KEYS, readFirst(readRaw(reservation), DATE_KEYS)),
   );
   if (!value) return "";
   const text = String(value).trim();
   const match = text.match(/(\d{4})-(\d{2})-(\d{2})/);
   return match ? match[0] : "";
}

export function readPracticalExamTime(reservation) {
   const value = readFirst(
      reservation,
      TIME_KEYS,
      readFirst(readSlot(reservation), TIME_KEYS, readFirst(readRaw(reservation), TIME_KEYS)),
   );
   if (!value) return "";

   const text = String(value).trim();
   const isoMatch = text.match(/[T\s](\d{1,2}):(\d{2})/);
   if (isoMatch) return normalizePracticalExamHour(`${isoMatch[1]}:${isoMatch[2]}`);
   return normalizePracticalExamHour(text);
}

function readGearboxLabel(reservation) {
   const value = readFirst(
      reservation,
      ["gearbox", "cutie"],
      readFirst(readSlot(reservation), ["gearbox"], readFirst(readRaw(reservation), ["gearbox"])),
   );
   const text = String(value || "").trim().toLowerCase();
   if (!text) return "";
   return text.startsWith("auto") ? "Automat" : "Manual";
}

function readInstructorName(reservation) {
   const instructor =
      readFirst(reservation, ["instructor", "teacher", "examiner"]) ||
      readFirst(readSlot(reservation), ["instructor", "teacher", "examiner"]) ||
      readFirst(readRaw(reservation), ["instructor", "teacher", "examiner"]);

   const direct = readFirst(reservation, ["instructorName", "instructor_name"]);
   if (direct) return String(direct).trim();

   if (!instructor || typeof instructor !== "object") return "";
   const first = String(readFirst(instructor, ["firstName", "firstname", "first_name"], "")).trim();
   const last = String(readFirst(instructor, ["lastName", "lastname", "last_name"], "")).trim();
   const full = `${first} ${last}`.trim();
   return full || String(readFirst(instructor, ["name", "fullName"], "")).trim();
}

function formatDateLabel(dateKey) {
   const match = String(dateKey || "").match(/(\d{4})-(\d{2})-(\d{2})/);
   if (!match) return "—";
   return `${match[3]}.${match[2]}.${match[1]}`;
}

function sortMsFor(dateKey, time) {
   if (!dateKey) return 0;
   const ms = Date.parse(`${dateKey}T${time || "00:00"}:00Z`);
   return Number.isFinite(ms) ? ms : 0;
}

export function normalizeStudentPracticalExam(reservation, index = 0) {
   const dateKey = readPracticalExamDateKey(reservation);
   const time = readPracticalExamTime(reservation);
   const status = readPracticalExamReservationStatus(reservation);
   const id = resolvePracticalExamReservationApiId(reservation);
   const sector = normalizePracticalExamSector(
      readFirst(
         reservation,
         ["sector"],
         readFirst(readSlot(reservation), ["sector"], readFirst(readRaw(reservation), ["sector"])),
      ),
      "",
   );

   return {
      id: id == null ? "" : String(id).trim(),
      key: id ? `practical-${String(id)}` : `practical-${dateKey}-${time}-${index}`,
      dateKey,
      time,
      dateLabel: formatDateLabel(dateKey),
      timeLabel: time || "—",
      sector,
      gearbox: readGearboxLabel(reservation),
      instructorName: readInstructorName(reservation),
      status,
      statusLabel: getPracticalExamStatusLabel(status),
      sortMs: sortMsFor(dateKey, time),
   };
}

export function collectStudentPracticalExams(rows, studentId) {
   const target = String(studentId ?? "").trim();
   if (!target) return [];

   const out = [];
   const seen = new Set();

   (Array.isArray(rows) ? rows : []).forEach((row, index) => {
      if (!row) return;
      if (readPracticalExamStudentId(row) !== target) return;

      const item = normalizeStudentPracticalExam(row, index);
      const dedupeKey = item.id || `${item.dateKey}|${item.time}|${item.status}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      out.push(item);
   });

   return out.sort((a, b) => b.sortMs - a.sortMs);
}

async function fetchReservationsAcrossStatuses(filters) {
   const results = await Promise.allSettled([
      getAdminPracticalExamReservations(filters),
      ...STATUS_FILTERS.map((status) =>
         getAdminPracticalExamReservations({ ...filters, status }),
      ),
   ]);

   const fulfilled = results.filter((result) => result.status === "fulfilled");
   if (!fulfilled.length) {
      const failed = results.find((result) => result.status === "rejected");
      throw failed?.reason || new Error("Nu am putut încărca examenele practice.");
   }

   return fulfilled.flatMap((result) =>
      Array.isArray(result.value?.items) ? result.value.items : [],
   );
}

export async function fetchStudentPracticalExams(studentId) {
   const target = String(studentId ?? "").trim();
   if (!target) return [];

   // Cerem intai cu filtrul pe elev: daca backend-ul il stie, raspunsul e mic.
   // Daca il ignora, filtram noi; daca il respinge, reluam fara filtru.
   let rows;
   try {
      rows = await fetchReservationsAcrossStatuses({ userId: target });
   } catch {
      rows = await fetchReservationsAcrossStatuses({});
   }

   return collectStudentPracticalExams(rows, target);
}
